import React, { useEffect, useState, useRef } from "react";
import api, { fileUrl, uploadImage, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Button, Modal, Field, Input, Empty, Badge } from "@/components/ui-kit";
import { Plus, Trash2, Pencil, Megaphone, Camera, Users, CalendarDays, Clock, MapPin, Swords, Dumbbell, GraduationCap, PartyPopper, Check, X, UserCheck, FileText, Bell } from "lucide-react";
import { toast } from "sonner";
import { DirectionsButtons } from "@/components/DirectionsButtons";
import { CommentsThread } from "@/components/CommentsThread";

const emptyAnn = { title: "", text: "", image: "", categories: [], group: "all", start_date: "", end_date: "" };
const GROUP_META = { gr1: { label: "GR 1", color: "purple" }, gr2: { label: "GR 2", color: "blue" } };
const EV_TYPES = {
  training: { label: "Entraînement", icon: Dumbbell },
  match: { label: "Match", icon: Swords },
  stage: { label: "Stage", icon: GraduationCap },
  event: { label: "Événement", icon: PartyPopper },
};

export default function Actualites() {
  const { user, can } = useAuth();
  const canEdit = can("announcements_manage");
  const canConfirm = can("attendance_manage");
  const canExport = can("data_export");
  const [anns, setAnns] = useState([]);
  const [feed, setFeed] = useState([]);
  const [locations, setLocations] = useState([]);
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyAnn);
  const [uploading, setUploading] = useState(false);
  const [notifyAll, setNotifyAll] = useState(false);
  const isParent = user?.role === "parent" || user?.role === "player";
  const [grFilter, setGrFilter] = useState("all");
  const grTouched = useRef(false);
  const [showArchived, setShowArchived] = useState(false);

  const load = () => api.get(`/announcements${showArchived ? "?archived=true" : ""}`).then((r) => setAnns(r.data));
  const loadFeed = () => api.get("/feed-events").then((r) => setFeed(r.data)).catch(() => {});
  useEffect(() => {
    load();
    loadFeed();
    api.get("/locations").then((r) => setLocations(r.data)).catch(() => {});
    if (canEdit) api.get("/categories").then((r) => setCats(r.data));
    if (isParent) api.get("/members").then((r) => {
      const gs = [...new Set((r.data || []).map((m) => m.group).filter(Boolean))];
      if (gs.length === 1 && !grTouched.current) setGrFilter(gs[0]);
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEdit, isParent, showArchived]);

  const shownAnns = anns.filter((a) => grFilter === "all" || !a.group || a.group === "all" || a.group === grFilter);

  const declare = async (eventId, memberId, declared) => {
    try {
      await api.post("/attendance/declare", { event_id: eventId, member_id: memberId, declared });
      setFeed((prev) => prev.map((e) => e.id === eventId ? {
        ...e, participants: e.participants.map((p) => p.member_id === memberId ? { ...p, declared } : p),
      } : e));
      toast.success(declared === "present" ? "Participation confirmée ✅" : "Absence enregistrée");
    } catch { toast.error("Action impossible"); }
  };

  const [rosterOpen, setRosterOpen] = useState(false);
  const [rosterEvent, setRosterEvent] = useState(null);
  const [roster, setRoster] = useState([]);
  const [rosterLoading, setRosterLoading] = useState(false);

  const NAMES_META = {
    present: { title: "Présents", dot: "bg-primary" },
    absent: { title: "Absents", dot: "bg-red-500" },
    pending: { title: "En attente de réponse", dot: "bg-muted-foreground" },
  };
  const [namesModal, setNamesModal] = useState(null); // {event, status}
  const [namesList, setNamesList] = useState([]);
  const [namesLoading, setNamesLoading] = useState(false);
  const [reminding, setReminding] = useState(false);

  const remindPending = async () => {
    if (!namesModal) return;
    setReminding(true);
    try {
      const { data } = await api.post(`/events/${namesModal.event.id}/remind-pending`);
      toast.success(data.notified ? `Rappel envoyé à ${data.notified} famille(s)` : "Aucune famille à relancer (aucun compte ou email lié)");
    } catch (err) { toast.error(err.response?.data?.detail || "Envoi impossible"); }
    finally { setReminding(false); }
  };

  const openNames = async (e, status) => {
    setNamesModal({ event: e, status });
    setNamesLoading(true); setNamesList([]);
    try {
      const { data } = await api.get(`/events/${e.id}/attendance-roster`);
      const r = data.roster || [];
      const filtered = status === "present" ? r.filter((m) => m.declared === "present")
        : status === "absent" ? r.filter((m) => m.declared === "absent")
        : r.filter((m) => !m.declared);
      setNamesList(filtered);
    } catch { toast.error("Chargement impossible"); }
    finally { setNamesLoading(false); }
  };

  const openRoster = async (e) => {
    setRosterEvent(e); setRosterOpen(true); setRosterLoading(true); setRoster([]);
    try {
      const { data } = await api.get(`/events/${e.id}/attendance-roster`);
      setRoster(data.roster || []);
    } catch { toast.error("Chargement impossible"); }
    finally { setRosterLoading(false); }
  };

  const confirmPresence = async (memberId, present) => {
    try {
      await api.post("/attendance/confirm", { event_id: rosterEvent.id, member_id: memberId, present });
      setRoster((prev) => prev.map((m) => m.member_id === memberId ? { ...m, coach_confirmed: true, present } : m));
      toast.success(present ? "Marqué présent" : "Marqué absent");
    } catch { toast.error("Action impossible"); }
  };

  const openNew = () => { setEditing(null); setForm(emptyAnn); setNotifyAll(false); setOpen(true); };
  const openEdit = (a) => { setEditing(a); setForm({ ...emptyAnn, ...a }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    if (editing) { await api.put(`/announcements/${editing.id}`, form); }
    else {
      await api.post("/announcements", form);
      if (notifyAll) {
        try {
          const payload = { scope: "all", title: "Nouvelle annonce", text: `📢 ${form.title}\n\n${form.text || ""}`.trim() };
          if (form.image) { payload.file_path = form.image; payload.file_name = "visuel.jpg"; payload.file_type = "image/jpeg"; }
          const { data } = await api.post("/messages/broadcast", payload);
          toast.success(`Diffusée dans la bulle de ${data.sent} membre(s)`);
        } catch { toast.error("Annonce publiée, mais diffusion bulle échouée."); }
      }
    }
    toast.success(editing ? "Annonce mise à jour" : "Annonce publiée");
    setOpen(false); load();
  };
  const remove = async (id) => { if (!window.confirm("Supprimer cette annonce ?")) return; await api.delete(`/announcements/${id}`); toast.success("Supprimée"); load(); };

  const onImg = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try { const path = await uploadImage(file); setForm((f) => ({ ...f, image: path })); toast.success("Image ajoutée"); }
    catch { toast.error("Échec de l'upload"); } finally { setUploading(false); }
  };

  const toggleCat = (name) => setForm((f) => {
    const cur = f.categories || [];
    return { ...f, categories: cur.includes(name) ? cur.filter((c) => c !== name) : [...cur, name] };
  });

  const sortedCats = [...cats].sort((a, b) => b.name.localeCompare(a.name, undefined, { numeric: true }));
  const fmtDate = (iso) => iso ? (String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/) ? (m => `${m[3]}/${m[2]}/${m[1].slice(-2)}`)(String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/)) : new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit" })) : "";
  const fmtEvDate = fmtDate;

  return (
    <div>
      <PageHeader title="Actualités" subtitle="Annonces et informations du club."
        action={canEdit && <Button data-testid="add-announcement-btn" onClick={openNew}><Plus size={16} /> Nouvelle annonce</Button>} />

      <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
        <h2 className="font-display text-lg font-bold uppercase tracking-wide flex items-center gap-2"><Megaphone size={18} className="text-primary" /> Annonces</h2>
        {canEdit && (
          <button data-testid="toggle-archived-anns" onClick={() => setShowArchived((v) => !v)}
            className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${showArchived ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
            {showArchived ? "← Annonces actives" : "Voir les archivées"}
          </button>
        )}
      </div>

      <div className="flex gap-2 flex-wrap mb-4" data-testid="ann-gr-filter">
        {[["all", "Tous groupes"], ["gr1", "GR 1"], ["gr2", "GR 2"]].map(([k, lbl]) => (
          <button key={k} data-testid={`ann-gr-${k}`} onClick={() => { grTouched.current = true; setGrFilter(k); }}
            className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${grFilter === k ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>{lbl}</button>
        ))}
      </div>

      {shownAnns.length ? (
        <div className="grid md:grid-cols-2 gap-5">
          {shownAnns.map((a) => (
            <Card key={a.id} data-testid="announcement-card" className="flex flex-col overflow-hidden p-0">
              {a.image && <img src={fileUrl(a.image)} alt={a.title} className="w-full h-44 object-cover" />}
              <div className="p-5 flex-1 flex flex-col">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="w-8 h-8 rounded-lg bg-primary/15 flex items-center justify-center text-primary shrink-0"><Megaphone size={16} /></span>
                    <p className="font-display text-lg font-bold leading-tight">{a.title}</p>
                  </div>
                  {canEdit && (
                    <div className="flex gap-1 shrink-0">
                      <button data-testid="edit-announcement-btn" onClick={() => openEdit(a)} className="p-1.5 text-muted-foreground hover:text-primary"><Pencil size={14} /></button>
                      <button data-testid="delete-announcement-btn" onClick={() => remove(a.id)} className="p-1.5 text-muted-foreground hover:text-destructive"><Trash2 size={14} /></button>
                    </div>
                  )}
                </div>
                {a.text && <p className="text-sm text-muted-foreground mt-2 whitespace-pre-line flex-1">{a.text}</p>}
                {(a.start_date || a.end_date) && (
                  <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1.5" data-testid={`ann-dates-${a.id}`}>
                    <CalendarDays size={12} className="text-primary" />
                    {a.start_date ? `Du ${fmtDate(a.start_date)}` : "Jusqu'au"}{a.end_date ? ` ${a.start_date ? "au" : ""} ${fmtDate(a.end_date)}` : ""}
                  </p>
                )}
                <div className="flex items-center justify-between gap-2 mt-4 pt-3 border-t border-border flex-wrap">
                  <div className="flex flex-wrap gap-1.5">
                    {a.is_archived && <Badge color="gray" data-testid={`ann-archived-${a.id}`}>Archivée</Badge>}
                    {(a.categories?.length ? a.categories : ["__all__"]).map((c) => (
                      <Badge key={c} color={c === "__all__" ? "primary" : "blue"}>{c === "__all__" ? "Tout le club" : c}</Badge>
                    ))}
                    {GROUP_META[a.group] && <Badge color={GROUP_META[a.group].color} data-testid={`ann-group-${a.id}`}>{GROUP_META[a.group].label}</Badge>}
                  </div>
                  <span className="text-xs text-muted-foreground">{fmtDate(a.created_at)}</span>
                </div>
                <CommentsThread targetType="announcement" targetId={a.id} count={a.comment_count} />
              </div>
            </Card>
          ))}
        </div>
      ) : <Empty text={showArchived ? "Aucune annonce archivée." : (canEdit ? "Publiez votre première annonce pour informer les membres." : "Aucune actualité pour le moment.")} />}

      {feed.length > 0 && (
        <div className="mt-10" data-testid="feed-events-section">
          <h2 className="font-display text-lg font-bold uppercase tracking-wide mb-4 flex items-center gap-2"><CalendarDays size={18} className="text-primary" /> Prochains événements confirmés</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {feed.map((e) => {
              const T = EV_TYPES[e.type] || EV_TYPES.event;
              const Icon = T.icon;
              return (
                <Card key={e.id} data-testid="feed-event-card" className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-8 h-8 rounded-lg bg-primary/15 flex items-center justify-center text-primary shrink-0"><Icon size={16} /></span>
                    <div className="min-w-0">
                      <p className="font-bold leading-tight truncate">{e.title}</p>
                      <p className="text-xs text-muted-foreground">{T.label}{e.category ? ` · ${e.category}` : ""}</p>
                    </div>
                    <Badge color="green" className="ml-auto">Confirmé</Badge>
                  </div>
                  {e.requires_convocation && (
                    <p className="text-[11px] text-muted-foreground -mt-1 mb-1 flex items-center gap-1"><UserCheck size={11} className="text-primary" /> Présence sur convocation du club</p>
                  )}
                  <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><CalendarDays size={13} /> {fmtEvDate(e.date)}{e.end_date ? ` → ${fmtEvDate(e.end_date)}` : ""}</span>
                    {e.time && <span className="flex items-center gap-1"><Clock size={13} /> {e.time}</span>}
                    {e.opponent && <span className="flex items-center gap-1"><Swords size={13} /> {e.opponent}</span>}
                  </div>
                  {e.location && (() => { const addr = locations.find((l) => l.id === e.location_id)?.address; return (
                    <div className="mt-2 flex items-start gap-2 flex-wrap text-xs text-muted-foreground">
                      <span className="flex items-start gap-1"><MapPin size={13} className="text-primary mt-0.5 shrink-0" /> <span>{e.location}{addr ? ` — ${addr}` : ""}</span></span>
                      <DirectionsButtons destination={addr || e.location} />
                    </div>
                  ); })()}
                    {e.counts && (canConfirm ? (
                    <div className="mt-2" data-testid="feed-event-counts">
                      <div className="flex flex-wrap items-center gap-2 text-xs">
                        <button data-testid={`count-present-${e.id}`} onClick={() => openNames(e, "present")}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/15 text-primary font-semibold hover:bg-primary/25 transition-colors"><Check size={12} /> {e.counts.present} présent{e.counts.present > 1 ? "s" : ""}</button>
                        <button data-testid={`count-absent-${e.id}`} onClick={() => openNames(e, "absent")}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/15 text-red-500 font-semibold hover:bg-red-500/25 transition-colors"><X size={12} /> {e.counts.absent} absent{e.counts.absent > 1 ? "s" : ""}</button>
                        <button data-testid={`count-pending-${e.id}`} onClick={() => openNames(e, "pending")}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-secondary text-muted-foreground hover:bg-secondary/70 transition-colors">{e.counts.pending} en attente</button>
                        <button data-testid={`open-roster-${e.id}`} onClick={() => openRoster(e)}
                          className="ml-auto text-primary font-semibold hover:underline inline-flex items-center gap-1"><UserCheck size={12} /> Confirmer</button>
                      </div>
                    </div>
                    ) : (
                    <div className="mt-2 flex flex-wrap gap-2 text-xs" data-testid="feed-event-counts">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/15 text-primary font-semibold"><Check size={12} /> {e.counts.present} présent{e.counts.present > 1 ? "s" : ""}</span>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/15 text-red-500 font-semibold"><X size={12} /> {e.counts.absent} absent{e.counts.absent > 1 ? "s" : ""}</span>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">{e.counts.pending} en attente</span>
                    </div>
                    ))}
                  {e.awaiting_convocation && (
                    <p className="mt-2 text-xs text-muted-foreground flex items-center gap-1.5" data-testid={`awaiting-convocation-${e.id}`}>
                      <UserCheck size={13} className="text-primary" /> Sur convocation du club — vous serez informé si votre enfant est convoqué.
                    </p>
                  )}
                  {e.participants?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-border space-y-2">
                      {e.participants.map((p) => (
                        <div key={p.member_id} className="flex items-center justify-between gap-2">
                          <span className="text-sm truncate">{p.member_name}</span>
                          <div className="flex gap-1 shrink-0">
                            <button data-testid={`participate-yes-${e.id}-${p.member_id}`} onClick={() => declare(e.id, p.member_id, "present")}
                              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold transition-colors ${p.declared === "present" ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
                              <Check size={13} /> Participe
                            </button>
                            <button data-testid={`participate-no-${e.id}-${p.member_id}`} onClick={() => declare(e.id, p.member_id, "absent")}
                              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold transition-colors ${p.declared === "absent" ? "bg-red-500 text-white" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
                              <X size={13} /> Absent
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {["match", "event", "stage"].includes(e.type) && (
                    <CommentsThread targetType="event" targetId={e.id} count={e.comment_count} />
                  )}
                </Card>
              );
            })}
          </div>
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Modifier l'annonce" : "Nouvelle annonce"}>
        <form onSubmit={save} data-testid="announcement-form">
          <div className="flex items-center gap-4 mb-4">
            {form.image ? (
              <img src={fileUrl(form.image)} alt="visuel" className="w-24 h-16 rounded-lg object-cover border border-border" />
            ) : (
              <div className="w-24 h-16 rounded-lg bg-secondary flex items-center justify-center text-muted-foreground"><Camera size={20} /></div>
            )}
            <label className="cursor-pointer">
              <span className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-border text-sm font-bold hover:bg-secondary transition-colors">
                <Camera size={15} /> {uploading ? "Envoi..." : "Image (optionnel)"}
              </span>
              <input data-testid="announcement-image-input" type="file" accept="image/*" className="hidden" onChange={onImg} disabled={uploading} />
            </label>
          </div>
          <Field label="Titre"><Input data-testid="announcement-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex. Tombola de mercredi" /></Field>
          <Field label="Message">
            <textarea data-testid="announcement-text" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })}
              rows={4} className="w-full rounded-lg bg-secondary border border-border p-3 text-sm outline-none focus:border-primary" placeholder="Ce mercredi, n'oubliez pas la tombola !" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Début d'annonce (optionnel)">
              <Input data-testid="announcement-start-date" type="date" value={form.start_date || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
            </Field>
            <Field label="Fin d'annonce (archivée le lendemain)">
              <Input data-testid="announcement-end-date" type="date" value={form.end_date || ""} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
            </Field>
          </div>
          <Field label="Destinataires (catégories) — vide = tout le club">
            <div className="flex flex-wrap gap-2" data-testid="announcement-categories">
              {sortedCats.map((c) => {
                const active = (form.categories || []).includes(c.name);
                return (
                  <button type="button" key={c.id} data-testid={`ann-cat-${c.name}`} onClick={() => toggleCat(c.name)}
                    className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${active ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
                    {c.name}
                  </button>
                );
              })}
            </div>
          </Field>
          <Field label="Groupe concerné">
            <div className="flex flex-wrap gap-2" data-testid="ann-group-select">
              {[["all", "Tous les groupes"], ["gr1", "GR 1"], ["gr2", "GR 2"]].map(([k, lbl]) => (
                <button type="button" key={k} data-testid={`ann-group-opt-${k}`} onClick={() => setForm({ ...form, group: k })}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${(form.group || "all") === k ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>{lbl}</button>
              ))}
            </div>
          </Field>
          {!editing && user?.role === "admin" && (
            <label data-testid="ann-notify-all" className="flex items-start gap-2 rounded-lg bg-secondary/40 p-3 cursor-pointer mt-1">
              <input type="checkbox" className="w-4 h-4 mt-0.5 accent-[color:var(--primary)]"
                checked={notifyAll} onChange={(e) => setNotifyAll(e.target.checked)} />
              <span className="text-xs">
                <b>Envoyer aussi dans la bulle de tous les membres</b>
                <span className="block text-muted-foreground">Le titre et le message (avec l'image) arrivent dans le chat interne + une notification pour chaque membre.</span>
              </span>
            </label>
          )}
          <Button data-testid="save-announcement-btn" className="w-full mt-2"><Megaphone size={16} /> {editing ? "Enregistrer" : "Publier"}</Button>
        </form>
      </Modal>

      <Modal open={rosterOpen} onClose={() => setRosterOpen(false)} title={`Présences · ${rosterEvent?.title || ""}`}>
        <div className="flex items-start justify-between gap-3 mb-3">
          <p className="text-sm text-muted-foreground">Confirmez qui est réellement présent ou absent. Le « déclaré » vient des familles.</p>
          {canExport && rosterEvent && (
            <Button data-testid="roster-pdf-btn" variant="outline" className="shrink-0"
              onClick={() => downloadFile(`/export/attendance/${rosterEvent.id}.pdf`, `presence_${rosterEvent.date || ""}.pdf`).catch(() => toast.error("Export échoué"))}>
              <FileText size={15} /> PDF
            </Button>
          )}
        </div>
        {rosterLoading ? <Empty text="Chargement..." /> : roster.length === 0 ? (
          <Empty text="Aucun membre dans cette catégorie." />
        ) : (
          <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
            {roster.map((m) => (
              <div key={m.member_id} data-testid="roster-row" className="flex items-center gap-2 p-2 rounded-lg bg-secondary/40">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{m.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {m.declared === "present" ? "Déclaré : présent" : m.declared === "absent" ? "Déclaré : absent" : "Pas de réponse"}
                  </p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button data-testid={`confirm-present-${m.member_id}`} onClick={() => confirmPresence(m.member_id, true)}
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold transition-colors ${m.coach_confirmed && m.present ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
                    <Check size={13} /> Présent
                  </button>
                  <button data-testid={`confirm-absent-${m.member_id}`} onClick={() => confirmPresence(m.member_id, false)}
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold transition-colors ${m.coach_confirmed && m.present === false ? "bg-red-500 text-white" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
                    <X size={13} /> Absent
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal>

      <Modal open={!!namesModal} onClose={() => setNamesModal(null)}
        title={namesModal ? `${NAMES_META[namesModal.status].title} · ${namesModal.event.title}` : ""}>
        {namesModal && (namesLoading ? <Empty text="Chargement..." /> : (
          <div data-testid="names-list">
            <div className="flex items-center justify-between gap-2 mb-3">
              <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{namesList.length} personne{namesList.length > 1 ? "s" : ""}</p>
              <div className="flex items-center gap-2 shrink-0">
                {canConfirm && namesModal.status === "pending" && namesList.length > 0 && (
                  <Button data-testid="names-remind-btn" variant="outline" className="h-8 px-3" disabled={reminding} onClick={remindPending}>
                    <Bell size={14} /> {reminding ? "Envoi..." : "Relancer"}
                  </Button>
                )}
                {canExport && (
                  <Button data-testid="names-pdf-btn" variant="outline" className="h-8 px-3"
                    onClick={() => downloadFile(`/export/attendance/${namesModal.event.id}.pdf?declared=${namesModal.status}`, `presence_${namesModal.status}_${namesModal.event.date || ""}.pdf`).catch(() => toast.error("Export échoué"))}>
                    <FileText size={14} /> PDF
                  </Button>
                )}
              </div>
            </div>
            {namesList.length === 0 ? (
              <Empty text="Aucune personne dans cette catégorie." />
            ) : (
              <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
                {namesList.map((m) => (
                  <div key={m.member_id} data-testid="names-row" className="flex items-center gap-2 p-2 rounded-lg bg-secondary/40">
                    <span className={`w-2 h-2 rounded-full shrink-0 ${NAMES_META[namesModal.status].dot}`} />
                    <span className="text-sm font-medium truncate flex-1">{m.name}</span>
                    {m.category && <span className="text-xs text-muted-foreground shrink-0">{m.category}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </Modal>
    </div>
  );
}
